
import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Heart, MessageCircle, Share2, Copy } from 'lucide-react';
import PromptAuthor from './PromptAuthor';
import SavePromptButton from './SavePromptButton';
import { Prompt } from '@/services/promptTypes';
import usePromptCard from '@/hooks/usePromptCard';

interface PromptCardProps {
  prompt: Prompt;
  category?: {
    name: string;
    slug: string;
  };
  onCopy?: (content: string) => void;
  onSave?: (promptId: string) => void;
  showPreview?: boolean;
  isPreviewMode?: boolean;
}

const PromptCard: React.FC<PromptCardProps> = ({ 
  prompt, 
  category, 
  onCopy, 
  onSave,
  showPreview = false,
  isPreviewMode = false
}) => {
  const {
    isLiked,
    likeCount,
    copyCount,
    isCopied,
    toggleLike,
    handleCopyContent,
  } = usePromptCard({
    id: prompt.id,
    content: prompt.content || '',
    initialLikes: prompt.likes || 0,
    initialCopies: prompt.copies || 0,
  });

  console.log('🔗 PromptCard: Rendering prompt card for ID:', prompt.id);

  // Format category if it's a JSON string or object
  const formatCategory = (categoryValue: string | any): string => {
    if (!categoryValue) return 'Uncategorized';

    if (typeof categoryValue === 'string') {
      // Try parsing as JSON if it looks like JSON
      if (categoryValue.startsWith('{')) {
        try {
          const categoryObj = JSON.parse(categoryValue);
          return categoryObj.name || 'Uncategorized';
        } catch (error) {
          // If parsing fails, return as is
          return categoryValue;
        }
      }
      return categoryValue;
    }
    
    if (typeof categoryValue === 'object' && categoryValue !== null) {
      if ('name' in categoryValue) {
        return categoryValue.name;
      }
    }
    
    return 'Uncategorized';
  };

  const categoryName = formatCategory(category?.name || prompt.category);

  const truncateDescription = (desc: string | undefined, maxLength = 120) => {
    if (!desc) return '';
    if (desc.length <= maxLength) return desc;
    return `${desc.slice(0, maxLength)}...`;
  };

  const handleCardClick = (e: React.MouseEvent) => {
    // Prevent navigation if clicking on action buttons
    const target = e.target as HTMLElement;
    if (target.closest('button') || target.closest('[data-stop-propagation="true"]')) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
  };

  const promptDetailUrl = `/prompts/${prompt.id}`;
  console.log('🔗 PromptCard: Link URL:', promptDetailUrl);

  return (
    <Link to={promptDetailUrl} className="block h-full" onClick={handleCardClick}>
      <Card className="h-full flex flex-col hover:shadow-lg transition-shadow duration-200 cursor-pointer">
        <CardHeader className="pb-4">
          <div className="flex items-start justify-between mb-3">
            <div className="flex-1 min-w-0">
              <h3 className="text-lg font-semibold line-clamp-2 mb-2">{prompt.title}</h3>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1 text-sm text-muted-foreground">
                <Copy className="h-4 w-4" />
                <span>{copyCount}</span>
              </div>
              <SavePromptButton
                promptId={prompt.id}
                promptTitle={prompt.title}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
              />
            </div>
          </div>
          <p className="text-sm text-muted-foreground line-clamp-3 mb-3">
            {truncateDescription(prompt.description)}
          </p>
          {categoryName && categoryName !== 'Uncategorized' && (
            <Badge variant="secondary" className="text-xs w-fit">
              {categoryName}
            </Badge>
          )}
        </CardHeader>
        
        <CardContent className="flex-1 flex flex-col justify-end pt-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  toggleLike(e);
                }}
                className="flex items-center gap-1 text-sm text-muted-foreground hover:text-green-600 transition-colors"
              >
                <Heart className={`h-4 w-4 ${isLiked ? 'fill-current text-green-600' : ''}`} />
                <span>{likeCount}</span>
              </button>
              
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  // Add message functionality here
                }}
                className="flex items-center gap-1 text-sm text-muted-foreground hover:text-primary transition-colors"
              >
                <MessageCircle className="h-4 w-4" />
                <span>{prompt.reviews || 0}</span>
              </button>
              
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  // Add share functionality here
                }}
                className="flex items-center gap-1 text-sm text-muted-foreground hover:text-primary transition-colors"
              >
                <Share2 className="h-4 w-4" />
                <span>Share</span>
              </button>
            </div>
            
            <PromptAuthor 
              id={prompt.author?.id || ''}
              name={prompt.author?.name || 'Unknown'}
              createdAt={prompt.createdAt}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
              }}
            />
          </div>
        </CardContent>
      </Card>
    </Link>
  );
};

export default PromptCard;
export type { PromptCardProps };
